import { useDispatch, useSelector } from "react-redux";
import { Row } from "../../../../core/components/Row";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Likes } from "../../../match/domain/entities/userMatch";
import { Chats } from "../../domain/entities/getChat";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { Column } from "../../../../core/components/Column";
import CircularProgress from "../../../../core/components/CircularProgress";
import { useCallback, useEffect, useState } from "react";
import { GetMyMatchesParams } from "../../../match/domain/usecases/getMyMatches";
import { onGetMyNewMatches } from "../../../match/presentation/store/MatchThunk";
import { getMatchLocationParams } from "../../../../core/utilities/locationStorage";
import { RemoteImage } from "../../../../core/components/RemoteImage";

const LIMITPAGINATION = "20";

export const MyNewMatches = ({ handleChatClick }: { handleChatClick: (chat: Chats) => void }) => {
	const dispatch: AppDispatch = useDispatch();
	const { myNewMatches } = useSelector((state: RootState) => state.match);
	const [currentPage, setCurrentPage] = useState(1);
	const [loadingMoreMatches, setLoadingMoreMatches] = useState(false);
	const [canLoadMoreMatches, setCanLoadMoreMatches] = useState(true);

	const handleHorizontalScroll = async (e: React.UIEvent<HTMLDivElement>) => {
		if (!canLoadMoreMatches || loadingMoreMatches) return;
		const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;
		if (scrollLeft + clientWidth >= scrollWidth - 5) {
			setLoadingMoreMatches(true);
			setCurrentPage((prevPage) => prevPage + 1);
			const result = await handleMyMatches((currentPage + 1).toString(), LIMITPAGINATION);
			const newMatches = result.payload as Likes[];
			if (newMatches.length === 0 || newMatches.length < Number(LIMITPAGINATION)) {
				setCanLoadMoreMatches(false);
			}
			setLoadingMoreMatches(false);
		}
	};

	const handleMyMatches = useCallback((page: string, limit: string) => {
		const params: GetMyMatchesParams = {
			params: {
				...getMatchLocationParams(),
				page: page,
				limit: limit,
			},
		};
		return dispatch(onGetMyNewMatches(params));
	}, [dispatch]);

	useEffect(() => {
		handleMyMatches("1", LIMITPAGINATION);
	}, [handleMyMatches]);

	useEffect(() => {
		setCanLoadMoreMatches(myNewMatches.length >= Number(LIMITPAGINATION));
	}, [myNewMatches.length]);

	return (
		<>
			{myNewMatches.length > 0 && (
				<>
					<Row className="sticky top-0 bg-white p-6 border-r border-b border-r-grayLight border-b-grayLight">
						<TextComponent
							variant="nh4"
							className="md:hidden lg:flex font-semibold text-primary"
						>
							New Matches
						</TextComponent>
					</Row>
					<Row
						className="border-r border-grayLight w-full h-52 md:h-48 overflow-y-hidden md:min-w-[4.3rem] md:max-w-[4.3rem] lg:min-w-[25rem] overflow-x-auto border-b border-b-grayLight p-2.5 lg:px-6 lg:py-4 gap-5"
						onScroll={handleHorizontalScroll}
					>
						{myNewMatches.map((match) => (
							<Column
								key={match.user_id}
								className="min-w-20 max-w-20 h-fit cursor-pointer"
								onClick={() => {
										handleChatClick(
											{
											chat_id: match.match_id,
											user_id: match.user_id,
											username: match.username,
											profile_pictures: [match.profile_pictures[0].url],
											message_sender_id: "",
											message_id: "",
											message_text: "",
											sent_at: "",
											read: false,
											unread_messages: 0,
											type: "text",
											show_last_logged: match.show_last_logged,
											last_logged_in_time: match.last_logged_in_time,
											status: "active",
											status_user_id1: "active",
												status_user_id2: "active",
											}
										);
								}}
							>
								<RemoteImage
									src={match.profile_pictures[0].url}
									alt="profile"
									draggable={false}
									className="select-none rounded-xl w-20 h-20 object-cover hover:opacity-90"
								/>
								<TextComponent
									variant="nh7"
									className="text-center lg:block font-medium overflow-hidden text-ellipsis whitespace-nowrap"
								>
									{match.username}
								</TextComponent>
							</Column>
						))}
						{loadingMoreMatches && <CircularProgress size={48} />}
					</Row>
				</>
			)}
		</>
	);
};
